import React from "react";

/**
 * Global ErrorBoundary.
 *
 * Wraps the entire application so a runtime crash in ANY component
 * (bad translation lookup, network races, dynamic-import failure, etc.)
 * cannot produce a fully blank white page on production mobile devices.
 *
 * Design choices
 * --------------
 * * We do NOT depend on react-i18next inside the fallback — if the i18n
 *   bundle failed to load, the boundary itself must still render. So the
 *   fallback UI carries hard-coded EN / ES / FR / HT strings, picked by
 *   inspecting `navigator.language`.
 * * We ship a unique Error ID (short base36 hash + timestamp) so the
 *   admin can grep the backend logs and correlate the crash. The ID is
 *   the ONLY thing the user needs to communicate.
 * * We attempt a best-effort POST to `/api/logs/frontend-error` with a
 *   sanitised report (browser, OS, viewport, route, error message).
 *   The report NEVER contains cookies, auth tokens, OTPs, passwords,
 *   or the full stack (backend caps + filters).
 * * "Retry" reloads with `?_r=<ts>` to bust the SW cache in case the
 *   crash was triggered by a stale asset bundle.
 * * "Home" navigates to `/` using an explicit assign() so it works even
 *   if react-router itself is what crashed.
 * * Errors are also logged to `console.error` for local devtools users.
 */
const FALLBACK_COPY = {
  en: {
    title: "We couldn't load this page",
    body:
      "Something went wrong while loading the app. This is usually temporary — please try again.",
    retry: "Retry",
    home: "Back to home",
    id_label: "Error ID",
  },
  es: {
    title: "No pudimos cargar esta página",
    body:
      "Algo salió mal al cargar la aplicación. Suele ser temporal — por favor, intente de nuevo.",
    retry: "Reintentar",
    home: "Volver al inicio",
    id_label: "ID de error",
  },
  fr: {
    title: "Impossible de charger la page",
    body:
      "Une erreur est survenue lors du chargement de l'application. C'est généralement temporaire — veuillez réessayer.",
    retry: "Réessayer",
    home: "Retour à l'accueil",
    id_label: "ID d'erreur",
  },
  ht: {
    title: "Nou pa t kapab chaje paj sa a",
    body:
      "Gen yon bagay ki mal pase pandan aplikasyon an ap chaje. Sa souvan tanporè — souple, eseye ankò.",
    retry: "Eseye ankò",
    home: "Retounen sou paj dakèy",
    id_label: "ID Erè",
  },
};

function pickLocale() {
  try {
    const stored = (localStorage.getItem("i18nextLng") || "").toLowerCase();
    if (stored.startsWith("es")) return "es";
    if (stored.startsWith("fr")) return "fr";
    if (stored.startsWith("ht") || stored.startsWith("hc")) return "ht";
    const nav = (navigator.language || "en").toLowerCase();
    if (nav.startsWith("es")) return "es";
    if (nav.startsWith("fr")) return "fr";
    if (nav.startsWith("ht") || nav.startsWith("hc")) return "ht";
  } catch {
    /* localStorage may be blocked (Safari private mode) */
  }
  return "en";
}

function makeErrorId() {
  // 6-char base36 + short timestamp — human-readable and low collision.
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  const ts = Date.now().toString(36).slice(-4).toUpperCase();
  return `ERR-${rand}-${ts}`;
}

async function reportError({ errorId, error, info }) {
  // Never include secrets. Only environment / route metadata + a very
  // short message. Backend rejects large payloads and rate-limits by IP.
  const payload = {
    error_id: errorId,
    route: (typeof window !== "undefined" && window.location.pathname) || "/",
    ua: (typeof navigator !== "undefined" && navigator.userAgent) || "",
    lang: (typeof navigator !== "undefined" && navigator.language) || "",
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : "",
    online:
      typeof navigator !== "undefined" ? Boolean(navigator.onLine) : true,
    // Cap message length so an accidental huge string doesn't hit us.
    message: String((error && error.message) || error || "").slice(0, 500),
    // Component stack is safer than the full JS stack — never any code paths.
    component_stack: String((info && info.componentStack) || "").slice(0, 2000),
    frontend_version:
      (typeof process !== "undefined" && process.env.REACT_APP_VERSION) ||
      "unknown",
    ts: new Date().toISOString(),
  };
  try {
    // Use fetch (not axios) so the boundary works even if axios crashed.
    const base =
      (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) ||
      "";
    await fetch(`${base}/api/logs/frontend-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      // Keep it short — user is already stuck on the fallback.
      keepalive: true,
    });
  } catch {
    /* offline or CORS — logging is best-effort */
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true, errorId: makeErrorId() };
  }

  componentDidCatch(error, info) {
    // Log to devtools for local diagnostics, without leaking to prod UI.
    console.error("[ErrorBoundary] crash caught", error, info);
    const errorId = this.state.errorId || makeErrorId();
    this.setState({ errorId });
    reportError({ errorId, error, info });
  }

  handleRetry = () => {
    // Bust any stale asset cache — many "white screen after deploy" bugs
    // come from a service worker holding an old chunk hash.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_r", String(Date.now()));
      window.location.assign(url.toString());
    } catch {
      window.location.reload();
    }
  };

  handleHome = () => {
    try {
      window.location.assign("/");
    } catch {
      window.location.href = "/";
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const copy = FALLBACK_COPY[pickLocale()] || FALLBACK_COPY.en;
    const errorId = this.state.errorId;
    return (
      <div
        data-testid="global-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background:
            "linear-gradient(135deg,#F8FAFC 0%,#EEF2FF 100%)",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: "#0F1B45",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "white",
            borderRadius: 16,
            boxShadow: "0 8px 32px rgba(15,27,69,0.08)",
            padding: "32px 28px",
            border: "1px solid #E2E8F0",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
            }}
            aria-hidden
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#DC2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              lineHeight: 1.25,
              fontFamily: "'Cabinet Grotesk', sans-serif",
            }}
          >
            {copy.title}
          </h1>
          <p
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: 14,
              lineHeight: 1.55,
              color: "#475569",
            }}
          >
            {copy.body}
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="error-retry-btn"
              style={{
                flex: 1,
                height: 44,
                borderRadius: 10,
                background: "#0F62FE",
                color: "white",
                border: 0,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {copy.retry}
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="error-home-btn"
              style={{
                flex: 1,
                height: 44,
                borderRadius: 10,
                background: "white",
                color: "#0F1B45",
                border: "1px solid #CBD5E1",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {copy.home}
            </button>
          </div>
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid #F1F5F9",
              fontSize: 11,
              color: "#94A3B8",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{copy.id_label}</span>
            <code
              data-testid="error-id"
              style={{
                fontFamily:
                  "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
                fontSize: 12,
                color: "#475569",
                background: "#F8FAFC",
                padding: "3px 8px",
                borderRadius: 6,
                border: "1px solid #E2E8F0",
              }}
            >
              {errorId}
            </code>
          </div>
        </div>
      </div>
    );
  }
}
